import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { RichTextEditor } from "../components/ui/rich-text-editor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { cn } from "../lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Search, Edit2, Trash2, Dumbbell, FolderOpen, Tag, X,
  ImageIcon, Upload, ChevronDown, Filter, Users, User, Loader2, Lock,
  Pencil, Check, ChevronLeft, ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function AllWorkouts() {
  const { hasPermission } = useAuth();
  const isDesktop = useIsDesktop();

  const canView = hasPermission("view_workouts");
  const canAdd = hasPermission("add_workout");
  const canEdit = hasPermission("edit_workout");
  const canDelete = hasPermission("delete_workout");

  const [workouts, setWorkouts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterTag, setFilterTag] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // staged filter state — committed on Apply
  const [stagedSearch, setStagedSearch] = useState("");
  const [stagedFilterGroup, setStagedFilterGroup] = useState("all");
  const [stagedFilterTag, setStagedFilterTag] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", image: "", groupIds: [], tagIds: [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteWorkout, setDeleteWorkout] = useState(null);
  const [assignedMembersWorkout, setAssignedMembersWorkout] = useState(null);

  useEffect(() => { if (canView) fetchMeta(); else setLoading(false); }, [canView]);

  const fetchMeta = async () => {
    try {
      const [groupsRes, tagsRes] = await Promise.all([
        api.get("/workouts/groups"),
        api.get("/workouts/tags"),
      ]);
      setGroups(groupsRes.data.groups || []);
      setTags(tagsRes.data.tags || []);
    } catch { toast.error("Failed to load filter options"); }
  };

  const fetchWorkouts = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search) params.append("search", search);
      if (filterGroup && filterGroup !== "all") params.append("groupId", filterGroup);
      if (filterTag && filterTag !== "all") params.append("tagId", filterTag);
      const res = await api.get(`/workouts?${params.toString()}`);
      setWorkouts(res.data.workouts || []);
      setTotal(res.data.total ?? res.data.workouts?.length ?? 0);
    } catch { toast.error("Failed to fetch workouts"); }
    finally { setLoading(false); }
  }, [page, limit, search, filterGroup, filterTag, canView]);

  useEffect(() => {
    const timer = setTimeout(() => fetchWorkouts(), 300);
    return () => clearTimeout(timer);
  }, [fetchWorkouts]);

  // sync staged state when sheet opens
  const handleOpenFilters = () => {
    setStagedSearch(search);
    setStagedFilterGroup(filterGroup);
    setStagedFilterTag(filterTag);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setSearch(stagedSearch);
    setFilterGroup(stagedFilterGroup);
    setFilterTag(stagedFilterTag);
    setPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setStagedSearch("");
    setStagedFilterGroup("all");
    setStagedFilterTag("all");
    setSearch("");
    setFilterGroup("all");
    setFilterTag("all");
    setPage(1);
    setShowFilters(false);
  };

  const handleOpenModal = (workout = null) => {
    if (workout) {
      setEditingWorkout(workout);
      setFormData({
        title: workout.title || "",
        description: workout.description || "",
        image: workout.image || "",
        groupIds: workout.groupIds || [],
        tagIds: workout.tagIds || [],
      });
    } else {
      setEditingWorkout(null);
      setFormData({ title: "", description: "", image: "", groupIds: [], tagIds: [] });
    }
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFormData((prev) => ({ ...prev, image: res.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Failed to upload image"); }
    finally { setUploading(false); }
  };



  const handleSubmit = async () => {
    if (!formData.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editingWorkout) {
        await api.put(`/workouts/${editingWorkout.id}`, formData);
        toast.success("Workout updated");
      } else {
        await api.post("/workouts", formData);
        toast.success("Workout created");
      }
      setShowModal(false);
      fetchWorkouts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save workout"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteWorkout) return;
    try {
      await api.delete(`/workouts/${deleteWorkout.id}`);
      toast.success("Workout deleted");
      setDeleteWorkout(null);
      fetchWorkouts();
    } catch { toast.error("Failed to delete workout"); }
  };

  const toggleRow = (id) => setExpandedRowId((prev) => (prev === id ? null : id));

  const renderWorkoutDetails = (workout) => {
    return (
      <div className="flex flex-col">
        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); setAssignedMembersWorkout(workout); }}
              data-testid={`assigned-members-btn-${workout.id}`}>
              <Users className="h-3.5 w-3.5" /> <span className="hidden md:block">Assigned Members</span>
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                onClick={(e) => { e.stopPropagation(); handleOpenModal(workout); }}
                data-testid={`edit-workout-${workout.id}`}>
                <Edit2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Edit Workout</span>
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                onClick={(e) => { e.stopPropagation(); setDeleteWorkout(workout); }}
                data-testid={`delete-workout-${workout.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete Workout</span>
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
          {/* Left Column: Image, Groups & Tags */}
          <div className="lg:col-span-4 space-y-6 p-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Workout Image</p>
              {workout.image ? (
                <img src={`${BACKEND_URL}${workout.image}`} alt={workout.title} className="w-full h-40 object-cover rounded-lg border border-border" />
              ) : (
                <div className="w-full h-40 bg-secondary rounded-lg flex items-center justify-center border border-border">
                  <Dumbbell className="h-10 w-10 text-muted-foreground opacity-50" />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Groups</p>
              <div className="flex flex-wrap gap-1.5">
                {workout.groupIds && workout.groupIds.length > 0 ? (
                  workout.groupIds.map(id => {
                    const g = groups.find(x => x.id === id);
                    if (!g) return null;
                    return (
                      <Badge key={id} variant="secondary" className="text-[10px] font-mono px-2 py-0.5 bg-blue-500/15 text-blue-500 border border-blue-500/20">
                        <FolderOpen className="h-3 w-3 mr-1" /> {g.name}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-xs italic text-muted-foreground">No groups assigned</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {workout.tagIds && workout.tagIds.length > 0 ? (
                  workout.tagIds.map(id => {
                    const t = tags.find(x => x.id === id);
                    if (!t) return null;
                    return (
                      <Badge key={id} variant="secondary" className="text-[10px] font-mono px-2 py-0.5 bg-pink-500/15 text-pink-500 border border-pink-500/20">
                        <Tag className="h-3 w-3 mr-1" /> {t.name}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-xs italic text-muted-foreground">No tags assigned</span>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Description */}
          <div className="lg:col-span-8 space-y-6 lg:border-l lg:border-border p-3 border-t md:border-t-0">
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Description</p>
              {workout.description ? (
                <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                  dangerouslySetInnerHTML={{ __html: workout.description }} />
              ) : (
                <p className="text-sm text-muted-foreground italic">No description provided</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeFilterCount = [
    search,
    filterGroup !== "all" ? filterGroup : "",
    filterTag !== "all" ? filterTag : "",
  ].filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;

  const hasActiveFilters = activeFilterCount > 0;
  const showActionsCol = canEdit || canDelete;

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="all-workouts-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">All Workouts</h1>
          <p className="text-sm text-muted-foreground">Manage your workout library</p>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-base">Access Restricted</p>
              <p className="text-sm text-muted-foreground mt-1">You don't have permission to view Workouts.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [
    { label: "TOTAL WORKOUTS", value: total, icon: Dumbbell },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="all-workouts-page">

      {/* ── Header ── */}
      <div className="flex sm:flex-row sm:items-center justify-between gap-2 md:gap-4 ">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">All Workouts</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={activeFilterCount > 0 ? "secondary" : "outline"} size="sm"
            onClick={handleOpenFilters} className="font-heading uppercase tracking-wide text-xs" data-testid="toggle-filters-btn">
            <Filter className="h-4 w-4" /> <span className="hidden sm:block">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>
          {canAdd && (
            <Button size="sm" onClick={() => handleOpenModal()} className="font-heading uppercase tracking-wide text-xs" data-testid="add-workout-btn">
              <Plus className="h-4 w-4 " /><span className="">Add Workout</span>
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-3 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statCards.map((s, i) => (
          // <Card key={i} className="bg-card border-border group hover:border-primary/40 transition-all min-w-[105px] md:min-w-0 flex-1 shrink-0">
          //   <CardContent className="p-2.5 sm:p-4">
          //     <div className="flex items-center gap-1 sm:gap-2">
          //       <s.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${s.color || "text-muted-foreground"} group-hover:text-primary transition-colors shrink-0`} />
          //       <p className="text-[8px] sm:text-[10px] text-muted-foreground font-mono uppercase tracking-wider sm:tracking-widest leading-tight truncate">{s.label}</p>
          //     </div>
          //     <p className={`text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 ${s.color || ""}`}>{s.value}</p>
          //   </CardContent>
          // </Card>
          <p key={i} className="text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5">Total {s.value}</p>
        ))}
      </div>

      {/* ── Filter Sheet — single instance, side driven by JS media query ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-[400px] flex flex-col p-0 bg-card border-border"
            : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{activeFilterCount} active</Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-muted-foreground text-xs h-7">
                  <X className="h-3.5 w-3.5 mr-1" />Clear all
                </Button>
              )}
            </div>
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Search */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search workouts..."
                  value={stagedSearch}
                  onChange={(e) => setStagedSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  className="pl-9"
                  data-testid="workout-search"
                />
              </div>
            </div>

            <Separator />

            {/* Group + Tag side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Group</Label>
                <Select value={stagedFilterGroup} onValueChange={setStagedFilterGroup}>
                  <SelectTrigger data-testid="filter-group"><SelectValue placeholder="All Groups" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Tag</Label>
                <Select value={stagedFilterTag} onValueChange={setStagedFilterTag}>
                  <SelectTrigger data-testid="filter-tag"><SelectValue placeholder="All Tags" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {tags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleApplyFilters}>
              <Search className="h-4 w-4 mr-2" />Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Table + Mobile Cards ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">

        {/* Desktop table */}
        <div className="hidden md:block overflow-auto flex-1">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10"></TableHead>
                <TableHead>Workout</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead>Tags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                      <span className="text-muted-foreground text-sm">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : workouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <Dumbbell className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {hasActiveFilters ? "No workouts match your filters." : "Get started by adding your first workout."}
                    </p>
                    {!hasActiveFilters && canAdd && (
                      <Button onClick={() => handleOpenModal()} className="mt-4" data-testid="empty-add-workout-btn">
                        <Plus className="h-4 w-4 mr-2" />Add Workout
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : workouts.map((workout) => (
                <React.Fragment key={workout.id}>
                  <TableRow className="group cursor-pointer hover:bg-secondary/50" data-testid={`workout-row-${workout.id}`} onClick={() => toggleRow(workout.id)}>
                    <TableCell className="w-10">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(workout.id); }}>
                        <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === workout.id ? "rotate-180" : ""}`} />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 overflow-hidden border border-border">
                          {workout.image
                            ? <img src={`${BACKEND_URL}${workout.image}`} alt={workout.title} className="h-full w-full object-cover" />
                            : <Dumbbell className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{workout.title}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {workout.groupIds && workout.groupIds.slice(0, 2).map(id => {
                          const g = groups.find(x => x.id === id);
                          if (!g) return null;
                          return (
                            <Badge key={id} variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-4 bg-blue-500/10 text-blue-500 border border-blue-500/20">
                              {g.name}
                            </Badge>
                          );
                        })}
                        {workout.groupIds && workout.groupIds.length > 2 && (
                          <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-4">
                            +{workout.groupIds.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {workout.tagIds && workout.tagIds.slice(0, 2).map(id => {
                          const t = tags.find(x => x.id === id);
                          if (!t) return null;
                          return (
                            <Badge key={id} variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-4 bg-pink-500/10 text-pink-500 border border-pink-500/20">
                              {t.name}
                            </Badge>
                          );
                        })}
                        {workout.tagIds && workout.tagIds.length > 2 && (
                          <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-4">
                            +{workout.tagIds.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedRowId === workout.id && (
                    <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border">
                      <TableCell colSpan={4} className="p-0">
                        {renderWorkoutDetails(workout)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* ── Mobile Card List ── */}
        <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
              <span className="text-muted-foreground text-sm ml-2">Loading...</span>
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No workouts found</div>
          ) : workouts.map((workout, idx) => (
            <Collapsible
              key={workout.id}
              open={expandedRowId === workout.id}
              onOpenChange={() => toggleRow(workout.id)}
              asChild
            >
              <div data-testid={`workout-card-${workout.id}`}
                className={` border-b border-border hover:bg-secondary/10 transition-colors`}>

                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="flex items-center justify-between flex-col">
                        <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0 overflow-hidden border border-border">
                          {workout.image ? (
                            <img src={`${BACKEND_URL}${workout.image}`} alt={workout.title} className="h-full w-full object-cover" />
                          ) : (
                            <Dumbbell className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0">
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === workout.id ? "rotate-180" : ""}`} />
                        </Button>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm leading-tight truncate">{workout.title}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {workout.groupIds && workout.groupIds.slice(0, 1).map(id => {
                            const g = groups.find(x => x.id === id);
                            if (!g) return null;
                            return (
                              <Badge key={id} variant="outline" className="text-[9px] font-mono px-1.5 py-0 h-4 bg-blue-500/10 text-blue-500 border border-blue-500/20">
                                {g.name}
                              </Badge>
                            );
                          })}
                          {workout.groupIds && workout.groupIds.length > 1 && (
                            <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0 h-4">
                              +{workout.groupIds.length - 1} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-end flex-col gap-0.5 text-right">
                        <div className="flex flex-wrap gap-1 max-w-[120px] justify-end">
                          {workout.tagIds && workout.tagIds.slice(0, 2).map(id => {
                            const t = tags.find(x => x.id === id);
                            if (!t) return null;
                            return (
                              <Badge key={id} variant="outline" className="text-[8px] font-mono uppercase px-1 py-0 h-3.5 bg-pink-500/10 text-pink-500 border border-pink-500/20">
                                {t.name}
                              </Badge>
                            );
                          })}
                          {workout.tagIds && workout.tagIds.length > 2 && (
                            <Badge variant="outline" className="text-[8px] font-mono px-1 py-0 h-3.5">
                              +{workout.tagIds.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                    {renderWorkoutDetails(workout)}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* ── Pagination ── */}
        <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono hidden sm:block">{total} total</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">Show:</span>
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft /></Button>
            <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {Math.ceil(total / limit) || 1}</span>
            <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}><ChevronRight /></Button>
          </div>
        </div>
      </Card>

      {/* ── Add/Edit Dialog ── */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!saving) setShowModal(open); }}>
        <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
            <DialogTitle className="font-heading uppercase tracking-wide">
              {editingWorkout ? "Edit Workout" : "Add Workout"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-2 py-2 md:px-4 md:py-4 space-y-2">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter workout title" disabled={saving} data-testid="workout-title-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Full Description</Label>
              <RichTextEditor value={formData.description}
                onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                placeholder="Detailed workout description…" data-testid="workout-desc-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Image</Label>
              <div className="flex items-center gap-4">
                {formData.image ? (
                  <div className="relative h-24 w-24 rounded-lg overflow-hidden bg-secondary">
                    <img src={`${BACKEND_URL}${formData.image}`} alt="Workout" className="h-full w-full object-cover" />
                    <button onClick={() => setFormData({ ...formData, image: "" })} disabled={saving}
                      className="absolute top-1 right-1 p-1 bg-background/80 rounded-full disabled:opacity-50">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-24 w-24 rounded-lg bg-secondary flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="workout-image-upload" disabled={saving} />
                  <label htmlFor="workout-image-upload">
                    <Button type="button" variant="outline" size="sm" disabled={uploading || saving} asChild>
                      <span>
                        {uploading
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                          : <><Upload className="h-4 w-4 mr-2" />Upload Image</>}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Groups</Label>
              <WorkoutGroupPicker
                groups={groups}
                setGroups={setGroups}
                selectedIds={formData.groupIds}
                onChange={ids => setFormData({ ...formData, groupIds: ids })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Tags</Label>
              <WorkoutTagPicker
                tags={tags}
                setTags={setTags}
                selectedIds={formData.tagIds}
                onChange={ids => setFormData({ ...formData, tagIds: ids })}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-border shrink-0 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving || uploading} data-testid="save-workout-btn">
              {saving
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingWorkout ? "Saving..." : "Creating..."}</>
                : editingWorkout ? "Save Changes" : "Create Workout"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteWorkout} onOpenChange={() => setDeleteWorkout(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">Delete Workout</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteWorkout?.title}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-workout">
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Assigned Members Dialog ── */}
      <AssignedMembersDialog workout={assignedMembersWorkout} onClose={() => setAssignedMembersWorkout(null)} />
    </div>
  );
}

function AssignedMembersDialog({ workout, onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workout) { setMembers([]); return; }
    setLoading(true);
    api.get(`/workouts/${workout.id}/assigned-members`)
      .then((res) => setMembers(res.data.members || []))
      .catch(() => toast.error("Failed to load assigned members"))
      .finally(() => setLoading(false));
  }, [workout]);

  return (
    <Dialog open={!!workout} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg flex flex-col p-0 gap-0 overflow-hidden max-h-[80vh]">
        <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <Users className="h-4 w-4" />Assigned Members
          </DialogTitle>
          {workout && <p className="text-xs text-muted-foreground mt-0.5">{workout.title}</p>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-16">
              <User className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">No members assigned</p>
              <p className="text-xs text-muted-foreground mt-1">This workout hasn&apos;t been assigned to any member yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {members.map((member) => (
                <div key={member.id} className="flex items-center gap-4 px-6 py-3 hover:bg-secondary/30 transition-colors">
                  <Avatar className="h-9 w-9 shrink-0">
                    {member.photo ? <AvatarImage src={`${BACKEND_URL}${member.photo}`} alt={member.name} /> : null}
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">{member.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{member.name}</p>
                    {member.email && <p className="text-xs text-muted-foreground truncate">{member.email}</p>}
                  </div>
                  {member.assignedDate && (
                    <span className="text-[11px] font-mono text-muted-foreground shrink-0">{member.assignedDate}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {!loading && members.length > 0 && (
          <div className="px-6 py-3 border-t border-border shrink-0">
            <p className="text-xs text-muted-foreground font-mono">
              {members.length} member{members.length !== 1 ? "s" : ""} assigned
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Workout Group inline multi-select + editable + searchable ──────────────────────────
function WorkoutGroupPicker({ groups, setGroups, selectedIds, onChange, placeholder = "Search or create...", createPlaceholder = "Create new group..." }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newText, setNewText] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const resolveChipStyle = (name) => {
    const colors = [
      { bg: "rgba(59, 130, 246, 0.1)", text: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.2)" },
      { bg: "rgba(99, 86, 255, 0.1)", text: "#6356ff", border: "1px solid rgba(99, 86, 255, 0.2)" },
      { bg: "rgba(16, 185, 129, 0.1)", text: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)" },
      { bg: "rgba(245, 158, 11, 0.1)", text: "#f59e0b", border: "1px solid rgba(245, 158, 11, 0.2)" },
      { bg: "rgba(236, 72, 153, 0.1)", text: "#ec4899", border: "1px solid rgba(236, 72, 153, 0.2)" },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const filtered = search.trim()
    ? groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : groups;

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const handleCreate = async () => {
    const text = newText.trim();
    if (!text) return;
    if (groups.find(g => g.name.toLowerCase() === text.toLowerCase())) {
      toast.error("Already exists");
      return;
    }
    try {
      const res = await api.post("/workouts/groups", { name: text });
      const newGroup = res.data.group;
      setGroups(prev => [...prev, newGroup]);
      onChange([...selectedIds, newGroup.id]);
      setNewText("");
      setSearch("");
    } catch {
      toast.error("Failed to create workout group");
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    if (groups.find(g => g.name.toLowerCase() === text.toLowerCase() && g.id !== id)) {
      toast.error("Another group with this name already exists");
      return;
    }
    try {
      await api.put(`/workouts/groups/${id}`, { name: text });
      setGroups(prev => prev.map(g => g.id === id ? { ...g, name: text } : g));
      setEditingId(null);
      setEditText("");
      toast.success("Group updated successfully");
    } catch {
      toast.error("Failed to update workout group");
    }
  };

  const selectedObjs = groups.filter(g => selectedIds.includes(g.id));
  const close = () => {
    setOpen(false);
    setSearch("");
    setEditingId(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            "min-h-[42px] w-full px-3 py-1.5 rounded-lg border text-sm flex flex-wrap items-center gap-1.5 cursor-text transition-all bg-background border-input hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary",
            open && "ring-2 ring-primary/20 border-primary"
          )}
        >
          {selectedObjs.map(g => {
            const c = resolveChipStyle(g.name);
            return (
              <span
                key={g.id}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: c.bg, color: c.text, border: c.border }}
              >
                <FolderOpen className="w-3.5 h-3.5 mr-0.5" />
                {g.name}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    toggle(g.id);
                  }}
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors ml-1"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={selectedIds.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-foreground placeholder:text-muted-foreground text-sm py-0.5"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border-border rounded-xl overflow-hidden"
        align="start"
        style={{ width: triggerWidth > 0 ? triggerWidth : "auto", minWidth: "240px" }}
      >
        <div className="p-3 border-b border-border space-y-2.5 bg-muted/20">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-input focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/25">
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search groups..."
              className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder={createPlaceholder}
              className="flex-1 h-8 px-3 text-xs rounded-lg border border-input bg-background outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-foreground placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!newText.trim()}
              className="h-8 px-3 rounded-lg text-xs font-semibold"
              size="sm"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[220px] p-1 space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nothing found &mdash; create one above
            </p>
          ) : (
            filtered.map(g => {
              const isSel = selectedIds.includes(g.id);
              const c = resolveChipStyle(g.name);
              return (
                <div
                  key={g.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors group",
                    isSel ? "bg-primary/10" : "hover:bg-muted"
                  )}
                >
                  {editingId === g.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") saveEdit(g.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-7 px-2 text-xs rounded border border-primary outline-none bg-background text-foreground"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggle(g.id)}
                      className="flex-1 text-left flex items-center gap-2"
                    >
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all"
                        style={{ background: c.bg, color: c.text, border: c.border }}
                      >
                        <FolderOpen className="w-3.5 h-3.5 mr-1" />
                        {g.name}
                      </span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {editingId === g.id ? (
                      <button
                        type="button"
                        onClick={() => saveEdit(g.id)}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-emerald-500/10"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingId(g.id);
                          setEditText(g.name);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-muted-foreground/10"
                      >
                        <Pencil className="w-3.5 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {isSel && editingId !== g.id && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="px-3.5 py-2.5 border-t border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:text-destructive/80 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Workout Tag inline multi-select + editable + searchable ──────────────────────────
function WorkoutTagPicker({ tags, setTags, selectedIds, onChange, placeholder = "Search or create...", createPlaceholder = "Create new tag..." }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newText, setNewText] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const resolveChipStyle = (name) => {
    const colors = [
      { bg: "rgba(236, 72, 153, 0.1)", text: "#ec4899", border: "1px solid rgba(236, 72, 153, 0.2)" },
      { bg: "rgba(16, 185, 129, 0.1)", text: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)" },
      { bg: "rgba(245, 158, 11, 0.1)", text: "#f59e0b", border: "1px solid rgba(245, 158, 11, 0.2)" },
      { bg: "rgba(59, 130, 246, 0.1)", text: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.2)" },
      { bg: "rgba(99, 86, 255, 0.1)", text: "#6356ff", border: "1px solid rgba(99, 86, 255, 0.2)" },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const filtered = search.trim()
    ? tags.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : tags;

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const handleCreate = async () => {
    const text = newText.trim();
    if (!text) return;
    if (tags.find(t => t.name.toLowerCase() === text.toLowerCase())) {
      toast.error("Already exists");
      return;
    }
    try {
      const res = await api.post("/workouts/tags", { name: text });
      const newTag = res.data.tag;
      setTags(prev => [...prev, newTag]);
      onChange([...selectedIds, newTag.id]);
      setNewText("");
      setSearch("");
    } catch {
      toast.error("Failed to create workout tag");
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    if (tags.find(t => t.name.toLowerCase() === text.toLowerCase() && t.id !== id)) {
      toast.error("Another tag with this name already exists");
      return;
    }
    try {
      await api.put(`/workouts/tags/${id}`, { name: text });
      setTags(prev => prev.map(t => t.id === id ? { ...t, name: text } : t));
      setEditingId(null);
      setEditText("");
      toast.success("Tag updated successfully");
    } catch {
      toast.error("Failed to update workout tag");
    }
  };

  const selectedObjs = tags.filter(t => selectedIds.includes(t.id));
  const close = () => {
    setOpen(false);
    setSearch("");
    setEditingId(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            "min-h-[42px] w-full px-3 py-1.5 rounded-lg border text-sm flex flex-wrap items-center gap-1.5 cursor-text transition-all bg-background border-input hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary",
            open && "ring-2 ring-primary/20 border-primary"
          )}
        >
          {selectedObjs.map(t => {
            const c = resolveChipStyle(t.name);
            return (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: c.bg, color: c.text, border: c.border }}
              >
                <Tag className="w-3.5 h-3.5 mr-0.5" />
                {t.name}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    toggle(t.id);
                  }}
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors ml-1"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={selectedIds.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-foreground placeholder:text-muted-foreground text-sm py-0.5"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border-border rounded-xl overflow-hidden"
        align="start"
        style={{ width: triggerWidth > 0 ? triggerWidth : "auto", minWidth: "240px" }}
      >
        <div className="p-3 border-b border-border space-y-2.5 bg-muted/20">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-input focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/25">
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder={createPlaceholder}
              className="flex-1 h-8 px-3 text-xs rounded-lg border border-input bg-background outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-foreground placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!newText.trim()}
              className="h-8 px-3 rounded-lg text-xs font-semibold"
              size="sm"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[220px] p-1 space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nothing found &mdash; create one above
            </p>
          ) : (
            filtered.map(t => {
              const isSel = selectedIds.includes(t.id);
              const c = resolveChipStyle(t.name);
              return (
                <div
                  key={t.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors group",
                    isSel ? "bg-primary/10" : "hover:bg-muted"
                  )}
                >
                  {editingId === t.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") saveEdit(t.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-7 px-2 text-xs rounded border border-primary outline-none bg-background text-foreground"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggle(t.id)}
                      className="flex-1 text-left flex items-center gap-2"
                    >
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all"
                        style={{ background: c.bg, color: c.text, border: c.border }}
                      >
                        <Tag className="w-3.5 h-3.5 mr-1" />
                        {t.name}
                      </span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {editingId === t.id ? (
                      <button
                        type="button"
                        onClick={() => saveEdit(t.id)}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-emerald-500/10"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingId(t.id);
                          setEditText(t.name);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-muted-foreground/10"
                      >
                        <Pencil className="w-3.5 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {isSel && editingId !== t.id && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="px-3.5 py-2.5 border-t border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:text-destructive/80 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
